import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrdenesPermisos } from '../../hooks/useOrdenesPermisos';
import { ordenesServicioAPI } from '../../lib/ordenesApi';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
    Calendar,
    ChevronLeft,
    ChevronRight,
    ArrowLeft,
    Clock,
    Wrench,
    CheckCircle,
    AlertCircle,
    User,
    Car,
    Building2,
    GripVertical,
    Eye,
} from 'lucide-react';

const ESTADO_CONFIG = {
    pendiente: { color: 'bg-yellow-100 text-yellow-800 border-yellow-300', icon: Clock, label: 'Pendiente' },
    asignada: { color: 'bg-indigo-100 text-indigo-800 border-indigo-300', icon: Clock, label: 'Asignada' },
    en_proceso: { color: 'bg-blue-100 text-blue-800 border-blue-300', icon: Wrench, label: 'En Proceso' },
    realizada: { color: 'bg-purple-100 text-purple-800 border-purple-300', icon: CheckCircle, label: 'Realizada' },
    facturada: { color: 'bg-green-100 text-green-800 border-green-300', icon: CheckCircle, label: 'Facturada' },
    cancelada: { color: 'bg-red-100 text-red-800 border-red-300', icon: AlertCircle, label: 'Cancelada' },
};

const PRIORIDAD_CONFIG = {
    alta: { color: 'border-l-red-500', label: 'Alta' },
    media: { color: 'border-l-yellow-500', label: 'Media' },
    baja: { color: 'border-l-gray-400', label: 'Baja' },
};

const DIAS_SEMANA = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

export default function OrdenesCalendarioPage() {
    const navigate = useNavigate();
    const { tienePermiso, loading: permisosLoading } = useOrdenesPermisos();
    const [loading, setLoading] = useState(true);
    const [ordenes, setOrdenes] = useState([]);
    const [currentDate, setCurrentDate] = useState(new Date());
    const [viewMode, setViewMode] = useState('week'); // week | month
    const [selectedOrden, setSelectedOrden] = useState(null);
    const [showDetailDialog, setShowDetailDialog] = useState(false);
    const [draggedOrden, setDraggedOrden] = useState(null);

    useEffect(() => {
        loadOrdenes();
    }, [currentDate, viewMode]);

    const loadOrdenes = async () => {
        try {
            setLoading(true);
            // Obtener órdenes que no estén completadas
            const res = await ordenesServicioAPI.getAll();
            setOrdenes(res.data.filter(o => !['facturada', 'cancelada'].includes(o.estado)));
        } catch (err) {
            console.error('Error loading ordenes:', err);
        } finally {
            setLoading(false);
        }
    };

    const getWeekDays = useCallback(() => {
        const days = [];
        const startOfWeek = new Date(currentDate);
        startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());
        
        for (let i = 0; i < 7; i++) {
            const day = new Date(startOfWeek);
            day.setDate(startOfWeek.getDate() + i);
            days.push(day);
        }
        return days;
    }, [currentDate]);

    const getMonthDays = useCallback(() => {
        const days = [];
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        
        // Días del mes anterior para completar la primera semana
        const startPadding = firstDay.getDay();
        for (let i = startPadding - 1; i >= 0; i--) {
            const day = new Date(year, month, -i);
            days.push({ date: day, isCurrentMonth: false });
        }
        
        // Días del mes actual
        for (let i = 1; i <= lastDay.getDate(); i++) {
            days.push({ date: new Date(year, month, i), isCurrentMonth: true });
        }
        
        // Días del mes siguiente para completar la última semana
        const endPadding = 42 - days.length; // 6 semanas * 7 días
        for (let i = 1; i <= endPadding; i++) {
            days.push({ date: new Date(year, month + 1, i), isCurrentMonth: false });
        }
        
        return days;
    }, [currentDate]);

    const getOrdenesForDate = (date) => {
        const dateStr = date.toISOString().split('T')[0];
        return ordenes.filter(o => {
            const ordenDate = o.fecha_programada?.split('T')[0];
            return ordenDate === dateStr;
        });
    };

    const navigateDate = (direction) => {
        const newDate = new Date(currentDate);
        if (viewMode === 'week') {
            newDate.setDate(newDate.getDate() + (direction * 7));
        } else {
            newDate.setMonth(newDate.getMonth() + direction);
        }
        setCurrentDate(newDate);
    };

    const goToToday = () => {
        setCurrentDate(new Date());
    };

    const handleDragStart = (e, orden) => {
        setDraggedOrden(orden);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    };

    const handleDrop = async (e, targetDate) => {
        e.preventDefault();
        if (!draggedOrden) return;
        
        try {
            const newFecha = targetDate.toISOString();
            await ordenesServicioAPI.update(draggedOrden.id, {
                fecha_programada: newFecha
            });
            toast.success(`Orden ${draggedOrden.numero_orden} reprogramada`);
            loadOrdenes();
        } catch (err) {
            toast.error('Error al reprogramar la orden');
        } finally {
            setDraggedOrden(null);
        }
    };

    const handleOrdenClick = (orden) => {
        setSelectedOrden(orden);
        setShowDetailDialog(true);
    };

    const isToday = (date) => {
        const today = new Date();
        return date.toDateString() === today.toDateString();
    };

    const formatDateHeader = () => {
        if (viewMode === 'week') {
            const weekDays = getWeekDays();
            const start = weekDays[0];
            const end = weekDays[6];
            if (start.getMonth() === end.getMonth()) {
                return `${start.getDate()} - ${end.getDate()} de ${MESES[start.getMonth()]} ${start.getFullYear()}`;
            }
            return `${start.getDate()} ${MESES[start.getMonth()].substring(0, 3)} - ${end.getDate()} ${MESES[end.getMonth()].substring(0, 3)} ${end.getFullYear()}`;
        }
        return `${MESES[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
    };

    if (permisosLoading) {
        return (
            <div className="space-y-6 p-6">
                <Skeleton className="h-10 w-64" />
                <Skeleton className="h-[600px] w-full" />
            </div>
        );
    }

    const OrdenCard = ({ orden, compact = false }) => {
        const estadoConfig = ESTADO_CONFIG[orden.estado] || ESTADO_CONFIG.pendiente;
        const prioridadConfig = PRIORIDAD_CONFIG[orden.prioridad] || PRIORIDAD_CONFIG.media;
        
        return (
            <div
                draggable
                onDragStart={(e) => handleDragStart(e, orden)}
                onClick={() => handleOrdenClick(orden)}
                className={`p-2 rounded-md border-l-4 ${prioridadConfig.color} bg-card shadow-sm cursor-pointer hover:shadow-md transition-shadow ${compact ? 'text-xs' : 'text-sm'}`}
            >
                <div className="flex items-start gap-1">
                    <GripVertical className="h-4 w-4 text-muted-foreground flex-shrink-0 cursor-grab" />
                    <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{orden.numero_orden}</p>
                        {!compact && (
                            <>
                                <p className="text-muted-foreground truncate">{orden.cliente?.razon_social}</p>
                                <div className="flex items-center gap-1 mt-1">
                                    <Badge className={`${estadoConfig.color} text-xs px-1 py-0`}>
                                        {estadoConfig.label}
                                    </Badge>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="space-y-4 p-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                    <Button variant="ghost" size="icon" onClick={() => navigate('/ordenes/dashboard')} data-testid="back-btn">
                        <ArrowLeft className="h-5 w-5" />
                    </Button>
                    <div>
                        <h1 className="text-2xl font-bold flex items-center gap-2">
                            <Calendar className="h-6 w-6" />
                            Calendario de Órdenes
                        </h1>
                        <p className="text-muted-foreground">Programación y asignación de trabajos</p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Select value={viewMode} onValueChange={setViewMode}>
                        <SelectTrigger className="w-[120px]">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="week">Semana</SelectItem>
                            <SelectItem value="month">Mes</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {/* Calendar Navigation */}
            <Card className="border-2">
                <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <Button variant="outline" size="icon" onClick={() => navigateDate(-1)}>
                                <ChevronLeft className="h-4 w-4" />
                            </Button>
                            <Button variant="outline" size="icon" onClick={() => navigateDate(1)}>
                                <ChevronRight className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" onClick={goToToday}>Hoy</Button>
                        </div>
                        <h2 className="text-lg font-semibold">{formatDateHeader()}</h2>
                        <div className="w-[100px]" /> {/* Spacer for alignment */}
                    </div>
                </CardHeader>
                <CardContent className="p-0">
                    {loading ? (
                        <div className="p-8">
                            <Skeleton className="h-[500px] w-full" />
                        </div>
                    ) : viewMode === 'week' ? (
                        /* Vista Semanal */
                        <div className="grid grid-cols-7 border-t">
                            {getWeekDays().map((day, idx) => (
                                <div 
                                    key={idx} 
                                    className={`border-r last:border-r-0 min-h-[400px] ${isToday(day) ? 'bg-blue-50/50' : ''}`}
                                    onDragOver={handleDragOver}
                                    onDrop={(e) => handleDrop(e, day)}
                                >
                                    <div className={`p-2 border-b text-center ${isToday(day) ? 'bg-blue-100' : 'bg-muted/30'}`}>
                                        <p className="text-xs text-muted-foreground">{DIAS_SEMANA[day.getDay()]}</p>
                                        <p className={`text-lg font-bold ${isToday(day) ? 'text-blue-600' : ''}`}>
                                            {day.getDate()}
                                        </p>
                                    </div>
                                    <div className="p-2 space-y-2 overflow-y-auto max-h-[350px]">
                                        {getOrdenesForDate(day).map(orden => (
                                            <OrdenCard key={orden.id} orden={orden} />
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        /* Vista Mensual */
                        <div className="grid grid-cols-7 border-t">
                            {/* Headers */}
                            {DIAS_SEMANA.map(dia => (
                                <div key={dia} className="p-2 border-r border-b last:border-r-0 text-center bg-muted/30">
                                    <p className="text-xs font-medium text-muted-foreground">{dia}</p>
                                </div>
                            ))}
                            {/* Days */}
                            {getMonthDays().map((dayInfo, idx) => (
                                <div 
                                    key={idx}
                                    className={`border-r border-b last:border-r-0 min-h-[100px] p-1 ${
                                        !dayInfo.isCurrentMonth ? 'bg-muted/20' : ''
                                    } ${isToday(dayInfo.date) ? 'bg-blue-50' : ''}`}
                                    onDragOver={handleDragOver}
                                    onDrop={(e) => handleDrop(e, dayInfo.date)}
                                >
                                    <p className={`text-xs mb-1 ${
                                        !dayInfo.isCurrentMonth ? 'text-muted-foreground' : ''
                                    } ${isToday(dayInfo.date) ? 'font-bold text-blue-600' : ''}`}>
                                        {dayInfo.date.getDate()}
                                    </p>
                                    <div className="space-y-1">
                                        {getOrdenesForDate(dayInfo.date).slice(0, 3).map(orden => (
                                            <OrdenCard key={orden.id} orden={orden} compact />
                                        ))}
                                        {getOrdenesForDate(dayInfo.date).length > 3 && (
                                            <p className="text-xs text-muted-foreground text-center">
                                                +{getOrdenesForDate(dayInfo.date).length - 3} más
                                            </p>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Órdenes sin programar */}
            <Card className="border-2">
                <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                        <AlertCircle className="h-4 w-4 text-yellow-500" />
                        Órdenes sin fecha programada
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="flex flex-wrap gap-2">
                        {ordenes.filter(o => !o.fecha_programada).length === 0 ? (
                            <p className="text-sm text-muted-foreground">Todas las órdenes tienen fecha programada</p>
                        ) : (
                            ordenes.filter(o => !o.fecha_programada).map(orden => (
                                <div
                                    key={orden.id}
                                    draggable
                                    onDragStart={(e) => handleDragStart(e, orden)}
                                    className="inline-flex items-center gap-2 px-3 py-2 bg-yellow-50 border border-yellow-200 rounded-md cursor-grab hover:bg-yellow-100 transition-colors"
                                >
                                    <GripVertical className="h-4 w-4 text-muted-foreground" />
                                    <span className="font-medium text-sm">{orden.numero_orden}</span>
                                    <span className="text-xs text-muted-foreground">{orden.cliente?.razon_social}</span>
                                </div>
                            ))
                        )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                        Arrastra las órdenes al calendario para programarlas
                    </p>
                </CardContent>
            </Card>

            {/* Detail Dialog */}
            <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            {selectedOrden?.numero_orden}
                            {selectedOrden && (
                                <Badge className={ESTADO_CONFIG[selectedOrden.estado]?.color}>
                                    {ESTADO_CONFIG[selectedOrden.estado]?.label}
                                </Badge>
                            )}
                        </DialogTitle>
                    </DialogHeader>
                    {selectedOrden && (
                        <div className="space-y-4">
                            <div className="flex items-center gap-3">
                                <Building2 className="h-4 w-4 text-muted-foreground" />
                                <div>
                                    <p className="font-medium">{selectedOrden.cliente?.razon_social}</p>
                                    <p className="text-sm text-muted-foreground">{selectedOrden.cliente?.rut}</p>
                                </div>
                            </div>
                            {selectedOrden.vehiculo && (
                                <div className="flex items-center gap-3">
                                    <Car className="h-4 w-4 text-muted-foreground" />
                                    <div>
                                        <p className="font-medium">{selectedOrden.vehiculo.patente}</p>
                                        <p className="text-sm text-muted-foreground">
                                            {selectedOrden.vehiculo.marca} {selectedOrden.vehiculo.modelo}
                                        </p>
                                    </div>
                                </div>
                            )}
                            {selectedOrden.tecnico && (
                                <div className="flex items-center gap-3">
                                    <User className="h-4 w-4 text-muted-foreground" />
                                    <div>
                                        <p className="font-medium">{selectedOrden.tecnico.nombre}</p>
                                        <p className="text-sm text-muted-foreground">Técnico asignado</p>
                                    </div>
                                </div>
                            )}
                            {selectedOrden.descripcion && (
                                <div className="p-3 bg-muted rounded-lg">
                                    <p className="text-sm">{selectedOrden.descripcion}</p>
                                </div>
                            )}
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowDetailDialog(false)}>Cerrar</Button>
                        <Button onClick={() => {
                            setShowDetailDialog(false);
                            navigate(`/ordenes/ordenes/${selectedOrden.id}`);
                        }}>
                            <Eye className="h-4 w-4 mr-2" />
                            Ver Detalle
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
